import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, channelIconName, channelColor } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { useT } from '@/i18n/labels';
import { useConversationStore } from '@/stores/conversationStore';
import { useAuthStore } from '@/stores/authStore';
import { useConsole } from '@/console/ConsoleContext';
import { getConversationLabel } from '@/utils/conversationLabel';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type { EscalationItemDto, AssignEscalationRequest } from '@/types';

const POLL_MS = 8000;

/** Priority string → design urgency class + label key. */
function urgencyOf(priority: string): { cls: string; key: 'urgency_high' | 'urgency_normal' | 'urgency_low' } {
  const p = (priority ?? '').toLowerCase();
  if (p === 'urgent' || p === 'high' || p === 'critical') return { cls: 'high', key: 'urgency_high' };
  if (p === 'low') return { cls: 'low', key: 'urgency_low' };
  return { cls: 'normal', key: 'urgency_normal' };
}

function fmtWait(sec: number, lang: string): string {
  const s = Math.max(0, Math.floor(sec));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  // OP-368 — roll over into hours/days so long waits stay readable
  // (a multi-hour wait used to render as raw "minutes:seconds", e.g. "10801:40").
  if (lang === 'he') {
    if (d > 0) return `${d} ימ׳ ${h} שע׳`;
    if (h > 0) return `${h} שע׳ ${m} דק׳`;
    return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r} ש׳`;
  }
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

/**
 * OP-376 — keep the live-queue preview to one short line. The `reason` can be
 * the bot's entire multi-sentence consultation ask, which blew the row height
 * up; clamp it and expose the full text via the row's title attribute.
 */
function clampReason(reason?: string | null): string {
  const s = (reason ?? '').trim();
  return s.length <= 140 ? s : s.slice(0, 140).trimEnd() + '…';
}

export const QueuePage: React.FC = () => {
  const { t, lang, channelLabel } = useT();
  const navigate = useNavigate();
  const conversations = useConversationStore((s) => s.conversations);
  const user = useAuthStore((s) => s.user);
  const { reloadSessions } = useConsole();

  const [items, setItems] = useState<EscalationItemDto[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [claiming, setClaiming] = useState<Record<string, boolean>>({});
  const [claimError, setClaimError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await axiosClient.get<EscalationItemDto[]>(API.ESCALATION.QUEUE);
      // Only surface still-claimable items (PENDING / ASSIGNED not yet resolved).
      const rows = (res.data ?? []).filter(
        (i) => !['RESOLVED', 'EXPIRED'].includes((i.status ?? '').toUpperCase()),
      );
      setItems(rows);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
    const poll = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(poll);
  }, [load]);

  // Live wait-time ticking (design behaviour).
  useEffect(() => {
    const i = window.setInterval(() => setTick((x) => x + 1), 1000);
    return () => window.clearInterval(i);
  }, []);
  void tick;

  const claim = async (item: EscalationItemDto) => {
    if (!user?.agentId) return;
    setClaiming((s) => ({ ...s, [item.id]: true }));
    setClaimError(null);
    try {
      const body: AssignEscalationRequest = { agentId: user.agentId };
      await axiosClient.post(API.ESCALATION.ASSIGN(item.id), body);
      setItems((rows) => rows.filter((r) => r.id !== item.id));
      await reloadSessions();
      // Navigate with the id rather than pre-setting the store: ConversationsPage hydrates a session
      // the list does not hold yet, which a just-claimed escalation frequently is.
      navigate(`/conversations/${encodeURIComponent(item.sessionId)}`);
    } catch {
      setClaimError(item.id);
      setClaiming((s) => ({ ...s, [item.id]: false }));
    }
  };

  const now = Date.now();

  return (
    <div className="ac-page ac-fade-up" data-testid="queue-page">
      <div className="ac-page-head" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <h1 className="ac-page-title" style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            {t('queue_title')}
            <span
              className="ac-status-dot online ac-pulse"
              style={{ width: 9, height: 9, borderRadius: '50%', color: 'var(--success)' }}
            />
          </h1>
          <div className="ac-page-sub">{t('queue_sub', { n: items.length })}</div>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="ac-card" style={{ padding: '12px 20px', marginBottom: 'var(--gap)', color: 'var(--warning)', fontWeight: 600 }}>
          {t('queue_load_error')}
        </div>
      )}

      {items.length === 0 ? (
        <div className="ac-card" style={{ padding: '60px 40px' }}>
          <div className="ac-empty" style={{ height: 'auto' }}>
            <div className="ac-empty-ico" style={{ background: 'var(--success-soft)', color: 'var(--success)' }}>
              <Icon name="check" size={28} />
            </div>
            <div className="ac-empty-title">{t('queue_empty')}</div>
            <div className="ac-empty-sub">{t('queue_empty_sub')}</div>
          </div>
        </div>
      ) : (
        <div className="ac-card">
          <div className="ac-table-row head">
            <div>{t('queue_customer')}</div>
            <div>{t('queue_channel')}</div>
            <div>{t('queue_wait')}</div>
            <div>{t('queue_priority')}</div>
            <div></div>
          </div>
          <div className="ac-stagger">
            {items.map((q) => {
              const conv = conversations.find((c) => c.sessionId === q.sessionId);
              const name = conv ? getConversationLabel(conv) : t('unidentified');
              const channel = conv?.channel ?? 'web';
              const waitSec = (now - new Date(q.createdAt).getTime()) / 1000;
              const u = urgencyOf(q.priority);
              const isClaiming = !!claiming[q.id];
              return (
                <div key={q.id} className="ac-table-row" data-testid={`queue-row-${q.id}`}>
                  <div className="ac-q-cust">
                    <Avatar name={name} seed={q.sessionId} size={38} className="ac-q-av" background={channelColor(channel)} />
                    <div style={{ minWidth: 0 }}>
                      <div className="ac-q-name">{name}</div>
                      <div className="ac-q-topic" title={q.reason || undefined}>
                        {clampReason(q.reason) || t('queue_reason')}
                      </div>
                    </div>
                  </div>
                  <div className="ac-q-ch">
                    <Icon name={channelIconName(channel)} size={16} style={{ color: channelColor(channel) }} />
                    {channelLabel(channel)}
                  </div>
                  <div className={'ac-wait' + (u.cls === 'high' ? ' high' : '')}>
                    <Icon name="clock" size={14} />
                    {fmtWait(waitSec, lang)}
                  </div>
                  <div>
                    <span className={'ac-badge ' + u.cls}>{t(u.key)}</span>
                  </div>
                  <div style={{ textAlign: 'end' }}>
                    <button
                      className="ac-btn grad sm"
                      disabled={isClaiming}
                      onClick={() => claim(q)}
                      data-testid={`queue-claim-${q.id}`}
                    >
                      {isClaiming ? t('queue_claiming') : t('queue_claim')}
                      {!isClaiming && <Icon name="arrow" size={14} className="ac-flip" />}
                    </button>
                    {claimError === q.id && (
                      <div role="alert" style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4, fontWeight: 600 }}>
                        {t('queue_claim_failed')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
